/**
 * SpeedrunTimer — Persistent timer with splits and best times.
 *
 * Tracks time from world creation to specific milestones:
 * - First Wood, First Stone Tool, First Iron, First Diamond,
 *   First Nether, Wither, First End, Dragon Kill.
 *
 * Displays an always-visible timer in top-left corner.
 * Saves personal bests per milestone (localStorage).
 */

const MILESTONES = [
  { id: 'wood',     label: 'Primera Madera' },
  { id: 'stone',    label: 'Herramientas de Piedra' },
  { id: 'iron',     label: 'Lingote de Hierro' },
  { id: 'diamond',  label: 'Primer Diamante' },
  { id: 'nether',   label: 'Entrada al Nether' },
  { id: 'blaze',    label: 'Blaze Rod' },
  { id: 'enderpearl',label: 'Perla de Ender' },
  { id: 'stronghold',label: 'Stronghold Encontrado' },
  { id: 'end',      label: 'Entrada al End' },
  { id: 'dragon',   label: 'Dragón Derrotado' },
];

const STORAGE_KEY = 'fedeworld_speedrun';
const BEST_KEY = 'fedeworld_speedrun_best';

export class SpeedrunTimer {
  constructor() {
    this.active = false;
    this.startTime = 0;
    this.splits = {}; // id -> ms elapsed
    this.bests = this._loadBests();
    this.lastUnlocked = null;
    this._buildUI();
  }

  _loadBests() {
    try {
      return JSON.parse(localStorage.getItem(BEST_KEY) || '{}');
    } catch { return {}; }
  }

  _saveBests() {
    localStorage.setItem(BEST_KEY, JSON.stringify(this.bests));
  }

  _buildUI() {
    // Persistent timer display (top-left, small)
    this._display = document.createElement('div');
    this._display.style.cssText = `
      position: fixed; top: 100px; left: 10px;
      background: rgba(0,0,0,0.7); padding: 6px 12px;
      font-family: 'Courier New', monospace; color: #FFD700;
      font-size: 14px; font-weight: bold; z-index: 150;
      border-left: 4px solid #FFD700; display: none;
      text-shadow: 1px 1px 0 #000;
    `;
    document.body.appendChild(this._display);

    // Splits panel (hidden by default, shown with /speedrun)
    this._overlay = document.createElement('div');
    this._overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.8); z-index: 919; display: none;
      justify-content: center; align-items: center;
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
      background: #2A2A2A; border: 4px solid #FFD700;
      padding: 24px; min-width: 440px;
      font-family: 'Courier New', monospace; color: #DDD;
    `;

    const title = document.createElement('div');
    title.innerHTML = '⏱ Speedrun Timer';
    title.style.cssText = 'font-size:20px;font-weight:bold;color:#FFD700;margin-bottom:16px;text-align:center;';
    panel.appendChild(title);

    this._currentTimeEl = document.createElement('div');
    this._currentTimeEl.style.cssText = 'font-size:28px;color:#FFF;text-align:center;margin-bottom:16px;font-weight:bold;';
    panel.appendChild(this._currentTimeEl);

    this._splitsListEl = document.createElement('div');
    this._splitsListEl.style.cssText = 'display:flex;flex-direction:column;gap:4px;margin-bottom:16px;';
    panel.appendChild(this._splitsListEl);

    const btns = document.createElement('div');
    btns.style.cssText = 'display:flex;gap:8px;';

    this._toggleBtn = document.createElement('div');
    this._toggleBtn.style.cssText = 'flex:1;padding:10px;text-align:center;cursor:pointer;font-weight:bold;background:#4CAF50;border:2px solid #6ACE70;';
    this._toggleBtn.addEventListener('click', () => {
      if (this.active) this.stop();
      else this.start();
      this._render();
    });
    btns.appendChild(this._toggleBtn);

    const closeBtn = document.createElement('div');
    closeBtn.textContent = 'Cerrar';
    closeBtn.style.cssText = 'flex:1;padding:10px;text-align:center;cursor:pointer;background:#555;border:2px solid #777;font-weight:bold;';
    closeBtn.addEventListener('click', () => this.hide());
    btns.appendChild(closeBtn);

    panel.appendChild(btns);
    this._overlay.appendChild(panel);
    document.body.appendChild(this._overlay);
  }

  start() {
    this.active = true;
    this.startTime = Date.now();
    this.splits = {};
    this._display.style.display = 'block';
  }

  stop() {
    this.active = false;
    this._display.style.display = 'none';
  }

  /**
   * Mark a milestone as reached.
   */
  mark(milestoneId) {
    if (!this.active || milestoneId in this.splits) return;
    const elapsed = Date.now() - this.startTime;
    this.splits[milestoneId] = elapsed;
    this.lastUnlocked = milestoneId;

    // Check personal best
    if (!this.bests[milestoneId] || elapsed < this.bests[milestoneId]) {
      this.bests[milestoneId] = elapsed;
      this._saveBests();
      if (this.onNewBest) this.onNewBest(milestoneId, elapsed);
    }
    if (this.onMilestone) this.onMilestone(milestoneId, elapsed);
  }

  update() {
    if (!this.active) return;
    const elapsed = Date.now() - this.startTime;
    this._display.textContent = '⏱ ' + this._formatTime(elapsed);
    if (this.lastUnlocked) {
      const best = this.bests[this.lastUnlocked];
      if (best) {
        this._display.innerHTML = `⏱ ${this._formatTime(elapsed)}<br><span style="font-size:10px;color:#AAA;">Último: ${this._getLabel(this.lastUnlocked)}</span>`;
      }
    }
  }

  _formatTime(ms) {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const ms100 = Math.floor((ms % 1000) / 10);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${ms100.toString().padStart(2, '0')}`;
  }

  _getLabel(id) {
    return MILESTONES.find(m => m.id === id)?.label || id;
  }

  _render() {
    this._toggleBtn.textContent = this.active ? 'Detener Run' : 'Iniciar Run';
    this._toggleBtn.style.background = this.active ? '#8B0000' : '#4CAF50';

    this._currentTimeEl.textContent = this.active ? this._formatTime(Date.now() - this.startTime) : '--:--:--.--';

    this._splitsListEl.innerHTML = '';
    for (const m of MILESTONES) {
      const split = this.splits[m.id];
      const best = this.bests[m.id];
      const row = document.createElement('div');
      row.style.cssText = `
        display:flex;justify-content:space-between;padding:6px 10px;
        background:${split ? '#2D4A2D' : '#3A3A3A'};
        border-left:3px solid ${split ? '#4CAF50' : '#555'};
      `;
      row.innerHTML = `
        <span style="font-size:12px;">${split ? '✓' : '○'} ${m.label}</span>
        <span style="font-size:11px;color:#AAA;">
          ${split ? this._formatTime(split) : '—'}
          ${best && best !== split ? ` · PB: ${this._formatTime(best)}` : ''}
        </span>
      `;
      this._splitsListEl.appendChild(row);
    }
  }

  show() {
    this._render();
    this._overlay.style.display = 'flex';
    this.isOpen = true;
    // Live update current time while panel is open
    this._panelInterval = setInterval(() => this._render(), 100);
  }

  hide() {
    this._overlay.style.display = 'none';
    this.isOpen = false;
    if (this._panelInterval) {
      clearInterval(this._panelInterval);
      this._panelInterval = null;
    }
  }
}
